type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;

// "seasonal" is a reserved set name
export const SEASONAL_SET_NAME = 'seasonal';

export const isSeasonalSetName = (name: string): boolean => name.trim().toLowerCase() === SEASONAL_SET_NAME;

// The lowest priority number places first, so the seasonal set wins a contested slot or design
export const SEASONALITY_PRIORITY = -1;

// Matches the default the search backend applies when a request does not define a seasonal set
export const DEFAULT_SEASONALITY_DISTRIBUTION: SetDistribution = {
    type: 'positions',
    positions: [4, 7],
    patternSize: 8,
};

// Omitting the set makes the backend apply a default (seasonality on), so off is written as an
// override that places nothing. The off set must not be exclusive: an exclusive set holds its
// designs away from global ranking and they would only show at the very end of the gallery.
const SEASONALITY_OFF_DISTRIBUTION: SetDistribution = { type: 'probability', part: 0 };

const isSeasonalityOffDistribution = (distribution: SetDistribution | undefined): boolean => (
    (distribution?.type === 'probability' || distribution?.type === 'backpressure') && distribution.part === 0
);

export type SeasonalitySetFilters = {
    seasonality: {
        isSeasonal: boolean;
        isRangeBound: boolean;
    };
};

export type SeasonalSetDefinition = Omit<SetDefinition, 'ranking' | 'filters'> & {
    exclusive: boolean;
    filters: SeasonalitySetFilters;
    groupMatchingMode: 'anyMatchToTop';
};

export const buildSeasonalSet = (
    distribution: SetDistribution = DEFAULT_SEASONALITY_DISTRIBUTION,
    exclusive = true,
): SeasonalSetDefinition => ({
    name: SEASONAL_SET_NAME,
    exclusive,
    priority: SEASONALITY_PRIORITY,
    distribution,
    filters: { seasonality: { isSeasonal: true, isRangeBound: true } },
    groupMatchingMode: 'anyMatchToTop',
});

export interface ExtractedSeasonality {
    seasonalityEnabled: boolean;
    seasonalityDistribution: SetDistribution;
    sets: SetDefinition[];
}

export const extractSeasonalityFromSets = (sets: SetDefinition[]): ExtractedSeasonality => {
    const index = sets.findIndex((set) => isSeasonalSetName(set.name));

    if (index === -1) {
        return {
            seasonalityEnabled: true,
            seasonalityDistribution: DEFAULT_SEASONALITY_DISTRIBUTION,
            sets,
        };
    }

    const { distribution } = sets[index] as Partial<SetDefinition>;
    const seasonalityEnabled = !isSeasonalityOffDistribution(distribution);

    return {
        seasonalityEnabled,
        seasonalityDistribution: seasonalityEnabled
            ? distribution ?? DEFAULT_SEASONALITY_DISTRIBUTION
            : DEFAULT_SEASONALITY_DISTRIBUTION,
        sets: [...sets.slice(0, index), ...sets.slice(index + 1)],
    };
};

// Seasonality set exists as an additional 'custom' set
export const injectSeasonalityIntoSets = (
    sets: SetDefinition[],
    seasonalityEnabled: boolean,
    seasonalityDistribution?: SetDistribution | null,
): SetDefinition[] => {
    const seasonalSet = seasonalityEnabled
        ? buildSeasonalSet(seasonalityDistribution ?? DEFAULT_SEASONALITY_DISTRIBUTION)
        : buildSeasonalSet(SEASONALITY_OFF_DISTRIBUTION, false);

    return [...sets, seasonalSet as Omit<SetDefinition, 'ranking'> as SetDefinition];
};
